"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aiVisibility = exports.aiVisibilityResultSchema = exports.scrapeResultSchema = exports.brandReportResultSchema = exports.aiVisibilityArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// AI Tool Types
var aiTools = ['ChatGPT', 'Gemini', 'Perplexity'];
// Mode Types
var modes = ['scrape', 'brand_report'];
exports.aiVisibilityArgsSchema = zod_1.z
    .object({
    mode: zod_1.z
        .enum(modes)
        .optional()
        .default('brand_report')
        .describe('Operation mode: "scrape" to extract ChatGPT answers for any query, "brand_report" to analyze brand visibility metrics.'),
    brandName: zod_1.z
        .string()
        .min(1)
        .optional()
        .nullable()
        .describe('Brand Name or Domain: Enter your brand name (e.g., "Nike", "Anthropic") or domain (e.g., "nike.com", "anthropic.com"). Domain format often provides more accurate and comprehensive results. URLs will be automatically normalized.'),
    query: zod_1.z
        .string()
        .min(1)
        .optional()
        .nullable()
        .describe('For scrape mode: The question or prompt to ask ChatGPT (e.g., "What are the most popular SEO blogs").'),
    aiTool: zod_1.z
        .enum(aiTools)
        .optional()
        .nullable()
        .default('ChatGPT')
        .describe('The AI platform to analyze. For scrape mode: only ChatGPT is supported. For brand_report mode: ChatGPT, Gemini, Perplexity are supported.'),
    location: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Optional: Add geographical location to get country-specific results. Available locations include country names or codes (e.g., "United States", "UK", "CA").'),
    competitors: zod_1.z
        .array(zod_1.z.string())
        .min(3)
        .max(5)
        .optional()
        .nullable()
        .describe('Optional: Specify 3-5 competitor URLs or brand names to perform a benchmark analysis. Helps compare your brand visibility against competitors.'),
    productsOrServices: zod_1.z
        .array(zod_1.z.string())
        .min(3)
        .max(10)
        .optional()
        .nullable()
        .describe('Optional: Specify 3-10 products or services that you offer. This helps the AI understand your business better for more accurate analysis.'),
    emitMode: common_1.emitModeSchema.default('single').describe('The mode to emit the data in.'),
    options: zod_1.z
        .object({
        failIfEmpty: zod_1.z
            .boolean()
            .default(true)
            .describe('Fail if the search returns no results. Default to true.'),
    })
        .optional()
        .nullable()
        .describe('Search options'),
})
    .refine(function (data) {
    if (data.mode === 'scrape') {
        return !!data.query && data.query.length > 0;
    }
    else {
        return !!data.brandName && data.brandName.length > 0;
    }
}, {
    message: 'For scrape mode, query is required. For brand_report mode, brandName is required.',
    path: ['mode'],
});
// Brand Report Result Schema (existing functionality)
exports.brandReportResultSchema = zod_1.z.object({
    brandInput: zod_1.z.string().describe('The original brand name or domain provided by the user'),
    brand_name: zod_1.z.string().optional().nullable().describe('Name of the analyzed brand'),
    brand_description: zod_1.z.string().describe('Description of the brand/company'),
    company_url: zod_1.z.string().describe('The company URL as identified by the AI'),
    brand_awareness_category: zod_1.z
        .string()
        .describe('Brand awareness category (e.g., "Iconic", "Recognized", etc.)'),
    brand_awareness_score: zod_1.z.number().describe('Brand awareness score (0-100)'),
    brand_awareness_sources: zod_1.z.array(zod_1.z.string()).describe('Sources contributing to brand awareness'),
    // Support both field name variants for backwards compatibility
    sentiment_category: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Overall sentiment classification (e.g., "Positive", "Neutral", "Negative")'),
    sentiment_classification: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Overall sentiment classification (e.g., "Positive", "Neutral", "Negative")'),
    sentiment_information: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Explanation or supporting information for the sentiment classification'),
    sentiment_score: zod_1.z
        .number()
        .optional()
        .nullable()
        .describe('Numerical sentiment score (if available)'),
    // Support both field name variants for backwards compatibility
    credibility_category: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Overall credibility rating category (e.g., "High", "Medium", "Low")'),
    credibility_classification: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Overall credibility rating category (e.g., "High", "Medium", "Low")'),
    credibility_information: zod_1.z
        .string()
        .optional()
        .nullable()
        .describe('Explanation behind the credibility rating'),
    credibility_score: zod_1.z.number().optional().nullable().describe('Numerical credibility score'),
    services_products: zod_1.z
        .array(zod_1.z.string())
        .describe('List of products or services associated with the brand'),
    top_5_topics: zod_1.z
        .array(zod_1.z.string())
        .optional()
        .nullable()
        .describe('Most important topics associated with the brand'),
    related_prompts: zod_1.z.array(zod_1.z.string()).describe('Prompts associated with the brand'),
    top_5_competitors: zod_1.z
        .array(zod_1.z.string())
        .optional()
        .nullable()
        .describe('List of top competitor brands'),
    requestedPlatform: zod_1.z.string().describe('The AI platform that was requested for analysis'),
});
// Scrape Result Schema (new functionality)
exports.scrapeResultSchema = zod_1.z.object({
    query: zod_1.z.string().describe('The original query provided by the user'),
    extracted_content: zod_1.z.string().describe('The extracted answer content from ChatGPT'),
    content_type: zod_1.z.string().describe('Type of content (e.g., "text", "code", "list")'),
    word_count: zod_1.z.number().describe('Number of words in the extracted content'),
    has_code_blocks: zod_1.z.boolean().describe('Whether the content contains code blocks'),
    has_links: zod_1.z.boolean().describe('Whether the content contains external links'),
    extracted_at: zod_1.z.string().describe('Timestamp when the content was extracted'),
    requestedPlatform: zod_1.z
        .string()
        .describe('The AI platform that was used (always ChatGPT for scrape mode)'),
});
// Union result schema that handles both modes
exports.aiVisibilityResultSchema = zod_1.z.union([exports.brandReportResultSchema, exports.scrapeResultSchema]);
exports.aiVisibility = (0, common_1.createEmitModeToolMetadata)({
    id: 'aiVisibility',
    displayName: 'AI Visibility',
    displayDescription: 'Extract ChatGPT answers for any query (scrape mode) or analyze brand visibility and reputation across ChatGPT, Gemini, and Perplexity (brand report mode). Get comprehensive insights on brand awareness, sentiment, credibility, and related topics.',
    category: 'search',
    color: common_1.BRAND_COLORS.AI,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.AI),
    icon: 'search',
    argsSchema: exports.aiVisibilityArgsSchema,
}, exports.aiVisibilityResultSchema);
